import { reformatDisplayedPrice } from './priceUtils';
import {
    ProductEntity,
    ProductPaymentPlan,
    ProductType,
    VariationType,
} from './types';

export const DEFAULT_VARIATION_ID = 'defvar12';

type Price = { price: number; displayed_price: string };

export const findInitialVariation = (variations: VariationType[] = []) =>
    variations.find((variation) => variation.selected_options_values);

/**
 * Get the variation which represents the product without selected choices
 */
const findDefaultVariation = (product: ProductType) =>
    product.variations?.find((variation) => !variation.selected_options_values);

const getItemId = (product: ProductType, variation?: VariationType) => {
    const productId = product.identifier || product.sku;
    const productExternalId = product.external_id || '';
    const {
        identifier: variationId = DEFAULT_VARIATION_ID,
        external_id: variationExternalId = '',
    } = variation || findDefaultVariation(product) || {};

    return `${productId}_${variationId}:${productExternalId}_${variationExternalId}`;
};

const getProductPrice = (
    { price, displayed_price }: ProductType,
    paymentPlan: ProductPaymentPlan | null
): Price =>
    paymentPlan
        ? {
              price: paymentPlan.plan_price,
              displayed_price: paymentPlan.plan_displayed_price,
          }
        : { price, displayed_price };

const getVariationPrice = (
    { price, displayed_price, plans_prices }: VariationType,
    paymentPlan: ProductPaymentPlan | null
): Price => {
    const planPrices = plans_prices.find(
        (prices) => prices.id === paymentPlan?.id
    );
    return planPrices
        ? {
              price: planPrices.price,
              displayed_price: planPrices.displayed_price,
          }
        : { price, displayed_price };
};

const applySurcharge = (
    { price, displayed_price }: Price,
    surcharge: number
): Price => {
    if (!surcharge) {
        return { price, displayed_price };
    }
    const total = price + surcharge;
    return {
        price: total,
        displayed_price: reformatDisplayedPrice(displayed_price, total),
    };
};

// Merges product & variation data & payment plan prices & customizations surcharge
export const buildProductData = ({
    product,
    variation,
    paymentPlan,
    surcharge,
}: {
    product: ProductType;
    variation?: VariationType;
    paymentPlan: ProductPaymentPlan | null;
    surcharge: number;
}): ProductEntity => {
    const itemId = getItemId(product, variation);
    if (variation) {
        return {
            ...product,
            ...variation,
            ...applySurcharge(
                getVariationPrice(variation, paymentPlan),
                surcharge
            ),
            image: variation.images[0]?.image || product.image,
            itemId,
            productId: product.identifier,
        };
    }

    return {
        ...product,
        ...applySurcharge(getProductPrice(product, paymentPlan), surcharge),
        itemId,
        productId: product.identifier,
    };
};
